import { useState, useEffect } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import { usePageTitle } from '@components/layout/MainLayout'
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Tab,
  Tabs,
  LinearProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import {
  Edit,
  LocationOn,
  CalendarMonth,
  EmojiEvents,
  Explore,
  Star,
  TrendingUp,
  People,
  Share,
  Settings as SettingsIcon,
  Map as MapIcon,
  PersonOutline,
  Cake,
  Link as LinkIcon,
  Facebook,
  Instagram,
  Twitter,
  LinkedIn,
  YouTube,
  GitHub,
  Language as WebIcon,
  ContentCopy,
  CardGiftcard,
  Lock,
  ErrorOutline,
  Forum as ForumIcon,
} from '@mui/icons-material'
import { useAuthStore } from '@store/authStore'
import { useCachedTags } from '@hooks/useCachedTags'
import { profileApi } from '@api/endpoints/profile'
import type { UserProfileDto } from '@types/profile'
import { ProfileVisibility, AgeVisibility } from '@types/profile'
import { ProfileBlogTab } from '../components/ProfileBlogTab'

interface UserStats {
  level: number
  totalPoints: number
  nextLevelPoints: number
  adventuresCompleted: number
  adventuresCreated: number
  badgesEarned: number
  totalBadges: number
  cachesFound: number
  distanceTraveled: number
  rank: number
  joinedDate: string
  location: string
}

interface RecentActivity {
  id: string
  type: 'adventure_completed' | 'badge_earned' | 'cache_found' | 'adventure_created'
  title: string
  description: string
  timestamp: string
  icon: React.ReactNode
  points?: number
}

// Mock user stats
const mockStats: UserStats = {
  level: 24,
  totalPoints: 12940,
  nextLevelPoints: 15000,
  adventuresCompleted: 98,
  adventuresCreated: 12,
  badgesEarned: 37,
  totalBadges: 50,
  cachesFound: 245,
  distanceTraveled: 387.5,
  rank: 4,
  joinedDate: '2024-03-15',
  location: 'San Francisco, CA',
}

// Social media platform icons and URL generators
const platformIcons: Record<string, React.ReactElement> = {
  Facebook: <Facebook />,
  Instagram: <Instagram />,
  Twitter: <Twitter />,
  LinkedIn: <LinkedIn />,
  YouTube: <YouTube />,
  GitHub: <GitHub />,
  Other: <WebIcon />,
}

const PLATFORM_BASE_URLS: Record<string, string> = {
  Facebook: 'https://facebook.com/',
  Instagram: 'https://instagram.com/',
  Twitter: 'https://twitter.com/',
  LinkedIn: 'https://linkedin.com/',
  YouTube: 'https://youtube.com/',
  GitHub: 'https://github.com/',
}

const PLATFORM_PROFILE_URLS: Record<string, string> = {
  Facebook: 'https://facebook.com/',
  Instagram: 'https://instagram.com/',
  Twitter: 'https://twitter.com/',
  LinkedIn: 'https://linkedin.com/in/',
  YouTube: 'https://youtube.com/@',
  GitHub: 'https://github.com/',
}

function generateSocialMediaUrl(username: string, platform: string, isPrivate: boolean = false): string {
  // If private, return just the base platform URL
  if (isPrivate) {
    return PLATFORM_BASE_URLS[platform] || 'https://' + platform.toLowerCase() + '.com'
  }

  if (platform === 'Other') {
    // For "Other", ensure URL has protocol
    if (!username.startsWith('http://') && !username.startsWith('https://')) {
      return `https://${username}`
    }
    return username
  }

  const baseUrl = PLATFORM_PROFILE_URLS[platform]
  return baseUrl ? `${baseUrl}${username}` : username
}

// Mock recent activities
const mockActivities: RecentActivity[] = [
  {
    id: '1',
    type: 'badge_earned',
    title: 'Tech Savvy Badge Earned',
    description: 'Completed all digital tool challenges',
    timestamp: '2025-09-22T14:30:00',
    icon: <EmojiEvents sx={{ color: 'warning.main' }} />,
    points: 60,
  },
  {
    id: '2',
    type: 'adventure_completed',
    title: 'Historic Downtown Discovery Completed',
    description: 'Finished all 8 locations on the trail',
    timestamp: '2025-09-20T10:15:00',
    icon: <Explore sx={{ color: 'success.main' }} />,
    points: 150,
  },
  {
    id: '3',
    type: 'cache_found',
    title: 'Hidden Garden Cache Found',
    description: 'Discovered rare cache in Golden Gate Park',
    timestamp: '2025-09-18T16:45:00',
    icon: <MapIcon sx={{ color: 'info.main' }} />,
    points: 25,
  },
  {
    id: '4',
    type: 'adventure_created',
    title: 'Street Art Hunt Published',
    description: 'Created new adventure with 15 locations',
    timestamp: '2025-09-15T09:00:00',
    icon: <Star sx={{ color: 'secondary.main' }} />,
    points: 100,
  },
]

export default function ProfilePage() {
  usePageTitle('Profile')
  const { userId } = useParams<{ userId?: string }>()
  const { user } = useAuthStore()
  const [activeTab, setActiveTab] = useState(0)
  const [profileData, setProfileData] = useState<UserProfileDto | null>(null)
  const [isLoadingProfile, setIsLoadingProfile] = useState(true)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [copiedReferralCode, setCopiedReferralCode] = useState(false)

  // Use cached tags hook
  const { getTag, getTags, isLoading: isLoadingTags } = useCachedTags()

  const isOwnProfile = !userId || userId === user?.id
  const displayName = profileData?.displayName || (isOwnProfile ? user?.firstName || user?.userName : `User ${userId}`)

  // Handle copying referral code to clipboard
  const handleCopyReferralCode = async () => {
    if (profileData?.referralCode) {
      try {
        await navigator.clipboard.writeText(`U${profileData.referralCode}`)
        setCopiedReferralCode(true)
        setTimeout(() => setCopiedReferralCode(false), 2000)
      } catch (err) {
        console.error('Failed to copy referral code:', err)
      }
    }
  }

  // Helper function to check if a field should be visible based on privacy settings
  const isFieldVisible = (visibility: ProfileVisibility | undefined): boolean => {
    // Always show to own profile
    if (isOwnProfile) return true
    // If viewing another's profile, check visibility setting
    // Note: Backend should already filter, but we double-check here
    if (visibility === undefined) return true // Default to visible if not set
    // Public is always visible
    if (visibility === ProfileVisibility.Public) return true
    // TODO: Add FriendsOnly and FriendsAndGroupMembers checks when friendship/group status is available
    // return visibility === ProfileVisibility.Public ||
    //        (visibility === ProfileVisibility.FriendsOnly && isFriend) ||
    //        (visibility === ProfileVisibility.FriendsAndGroupMembers && (isFriend || isGroupMember))
    return false // Private and other restricted visibility levels are hidden
  }

  // Calculate level progress from points to next rank
  const levelProgress = profileData?.pointsToNextRank ?
    Math.max(0, Math.min(100, ((1000 - profileData.pointsToNextRank) / 1000) * 100)) :
    ((mockStats.totalPoints % 1000) / 1000) * 100

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoadingProfile(true)
        setProfileError(null)
        const data = isOwnProfile
          ? await profileApi.getMyProfile()
          : await profileApi.getUserProfile(userId!)
        setProfileData(data)
      } catch (error: any) {
        console.error('Error fetching profile:', error)
        // Check if it's a 404 error (profile not found or private)
        if (error?.response?.status === 404) {
          setProfileError(error?.response?.data?.message || 'User profile not found or is private')
        } else {
          setProfileError('Unable to load profile. Please try again later.')
        }
      } finally {
        setIsLoadingProfile(false)
      }
    }

    fetchProfile()
  }, [userId, isOwnProfile])

  return (
    <Box>
      {/* Loading State */}
      {isLoadingProfile && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      )}

      {/* Error State - Profile not found or private */}
      {!isLoadingProfile && profileError && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Lock sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
            Profile Unavailable
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {profileError}
          </Typography>
          <Button
            component={RouterLink}
            to="/"
            variant="contained"
            sx={{ mt: 2 }}
          >
            Return Home
          </Button>
        </Paper>
      )}

      {/* Cover Photo Banner - Only show if coverPhoto exists */}
      {!isLoadingProfile && !profileError && profileData?.coverPhoto && (
        <Box
          sx={{
            width: '100%',
            height: 300,
            backgroundImage: `url(${profileData.coverPhoto})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderRadius: '8px 8px 0 0',
            position: 'relative',
          }}
        >
          {/* Edit Profile and Settings buttons overlay on cover photo */}
          {isOwnProfile && (
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                display: 'flex',
                gap: 1,
              }}
            >
              <Button
                component={RouterLink}
                to="/profile/edit"
                variant="contained"
                startIcon={<Edit />}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                  },
                }}
              >
                Edit Profile
              </Button>
              <Button
                component={RouterLink}
                to="/settings"
                variant="contained"
                startIcon={<SettingsIcon />}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                  },
                }}
              >
                Settings
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Profile Header */}
      {!isLoadingProfile && !profileError && (
      <>
      <Paper
        sx={{
          px: 4,
          pb: 4,
          pt: profileData?.coverPhoto ? 0 : 4,
          mb: 3,
          mt: profileData?.coverPhoto ? '-60px' : 0,
          position: 'relative'
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item sx={{ mt: profileData?.coverPhoto ? '-120px' : 0 }}>
            <Avatar
              src={profileData?.avatar || undefined}
              sx={{
                width: 120,
                height: 120,
                fontSize: 48,
                border: '4px solid',
                borderColor: 'background.paper',
                boxShadow: 3,
              }}
            >
              {!profileData?.avatar && displayName ? displayName[0]?.toUpperCase() : '?'}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {displayName}
              </Typography>
              {profileData?.rankName && (
                <Chip
                  label={profileData.rankName}
                  color="primary"
                  sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                />
              )}
              {profileData?.rank !== undefined && (
                <Chip
                  label={`Rank ${profileData.rank}`}
                  color="secondary"
                  icon={<TrendingUp />}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 3, color: 'text.secondary', mb: 2, flexWrap: 'wrap' }}>
              {profileData?.hometown && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOn fontSize="small" />
                  <Typography variant="body2">{profileData.hometown}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarMonth fontSize="small" />
                <Typography variant="body2">
                  Joined {profileData?.createdDate
                    ? new Date(profileData.createdDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                    : new Date(mockStats.joinedDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </Typography>
              </Box>
            </Box>
            {/* Show buttons here only if NO cover photo */}
            {isOwnProfile && !profileData?.coverPhoto && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  component={RouterLink}
                  to="/profile/edit"
                  variant="outlined"
                  startIcon={<Edit />}
                  size="small"
                >
                  Edit Profile
                </Button>
                <Button
                  component={RouterLink}
                  to="/settings"
                  variant="outlined"
                  startIcon={<SettingsIcon />}
                  size="small"
                >
                  Settings
                </Button>
                <IconButton size="small">
                  <Share />
                </IconButton>
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Level Progress */}
        {profileData?.nextRankName && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                Progress to {profileData.nextRankName}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {profileData.pointsToNextRank ? (1000 - profileData.pointsToNextRank).toLocaleString() : 0} / 1000 XP
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={levelProgress}
              sx={{ height: 10, borderRadius: 5 }}
            />
          </Box>
        )}
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid', borderColor: 'primary.main' }}>
            <CardContent>
              <Explore sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {mockStats.adventuresCompleted}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid', borderColor: 'secondary.main' }}>
            <CardContent>
              <Star sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {mockStats.adventuresCreated}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Created
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid', borderColor: 'warning.main' }}>
            <CardContent>
              <EmojiEvents sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {mockStats.badgesEarned}/{mockStats.totalBadges}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Badges
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid', borderColor: 'success.main' }}>
            <CardContent>
              <MapIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {mockStats.cachesFound}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Caches Found
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Referral Code - Only visible on own profile */}
      {isOwnProfile && profileData?.referralCode && (
        <Paper
          sx={{
            p: 3,
            mb: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CardGiftcard sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Your Personal Code
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Share to invite friends or let others find and connect with you
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Paper
                sx={{
                  px: 3,
                  py: 1.5,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 'bold',
                    fontFamily: 'monospace',
                    color: '#667eea',
                    letterSpacing: 2,
                  }}
                >
                  U{profileData.referralCode}
                </Typography>
              </Paper>
              <Button
                variant="contained"
                startIcon={copiedReferralCode ? <Star /> : <ContentCopy />}
                onClick={handleCopyReferralCode}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                {copiedReferralCode ? 'Copied!' : 'Copy Code'}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Additional Stats */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          Adventure Statistics
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Total Points Earned
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {mockStats.totalPoints.toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Distance Traveled
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {mockStats.distanceTraveled} miles
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Global Rank
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="primary">
                #{mockStats.rank}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Completion Rate
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {Math.round((mockStats.adventuresCompleted / (mockStats.adventuresCompleted + 20)) * 100)}%
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Badge Collection
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {Math.round((mockStats.badgesEarned / mockStats.totalBadges) * 100)}%
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Days Active
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {profileData?.createdDate
                  ? Math.floor((new Date().getTime() - new Date(profileData.createdDate).getTime()) / (1000 * 60 * 60 * 24))
                  : Math.floor((new Date().getTime() - new Date(mockStats.joinedDate).getTime()) / (1000 * 60 * 60 * 24))}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="About" icon={<PersonOutline />} iconPosition="start" />
          <Tab label="Recent Activity" />
          <Tab label="Blog" icon={<ForumIcon />} iconPosition="start" />
          <Tab label="Badges" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3 }}>
          {isLoadingProfile || isLoadingTags ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Bio Section with Social Media */}
              {profileData?.bio && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Bio
                      </Typography>
                      <Typography variant="body1" color="text.secondary" paragraph>
                        {profileData.bio}
                      </Typography>
                    </Box>
                    {/* Social Media Icons */}
                    {(() => {
                      const socialLinks = profileData?.socialMediaLinks || {}
                      const visibleLinks = Object.entries(socialLinks)
                        .filter(([_, link]) => link.url)
                        .filter(([_, link]) => isFieldVisible(link.visibility))

                      return visibleLinks.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                          {visibleLinks.map(([key, link]) => {
                            return (
                              <Tooltip key={key} title={link.platform} arrow placement="top">
                                <IconButton
                                  component="a"
                                  href={generateSocialMediaUrl(link.url, link.platform, false)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  color="primary"
                                  size="medium"
                                >
                                  {platformIcons[link.platform] || <LinkIcon />}
                                </IconButton>
                              </Tooltip>
                            )
                          })}
                        </Box>
                      )
                    })()}
                  </Box>
                  <Divider sx={{ my: 2 }} />
                </Grid>
              )}

              {/* Personal Information - 3 Column Layout */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                  Personal Information
                </Typography>
                <Grid container spacing={3}>
                  {/* Column 1: Member Since & Age */}
                  <Grid item xs={12} md={4}>
                    {/* Member Since */}
                    {profileData?.createdDate && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Member Since
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <CalendarMonth fontSize="small" color="action" />
                          <Typography variant="body1">
                            {new Date(profileData.createdDate).toLocaleDateString('en-US', {
                              month: 'long',
                              year: 'numeric',
                            })}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    {/* Age */}
                    {(() => {
                      // Calculate age display based on AgeVisibility setting
                      const getAgeDisplay = (): string | null => {
                        if (!profileData?.ageInMonths) return null

                        const ageYears = Math.floor(profileData.ageInMonths / 12)
                        const ageMonths = profileData.ageInMonths % 12
                        const birthYear = new Date().getFullYear() - ageYears - (ageMonths > 0 ? 1 : 0)

                        switch (profileData.showAge) {
                          case AgeVisibility.Private:
                            return null
                          case AgeVisibility.AgeOnly:
                            return `${ageYears}`
                          case AgeVisibility.MonthAge:
                            return ageMonths > 0
                              ? `${ageYears} years, ${ageMonths} ${ageMonths === 1 ? 'month' : 'months'}`
                              : `${ageYears} years`
                          case AgeVisibility.MonthYearAge:
                            const baseAge = ageMonths > 0
                              ? `${ageYears} years, ${ageMonths} ${ageMonths === 1 ? 'month' : 'months'}`
                              : `${ageYears} years`
                            return `${baseAge} (Born ${birthYear})`
                          default:
                            return null
                        }
                      }

                      const ageDisplay = getAgeDisplay()
                      return ageDisplay && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Age
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Cake fontSize="small" color="action" />
                            <Typography variant="body1">{ageDisplay}</Typography>
                          </Box>
                        </Box>
                      )
                    })()}
                    {/* Hometown */}
                    {profileData?.hometown && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Hometown
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <LocationOn fontSize="small" color="action" />
                          <Typography variant="body1">{profileData.hometown}</Typography>
                        </Box>
                      </Box>
                    )}
                  </Grid>

                  {/* Column 2: Gender Identity & Looking For */}
                  <Grid item xs={12} md={4}>
                    {/* Gender Identity */}
                    {(() => {
                      const tagId = profileData?.genderIdentityTagId
                      const tag = tagId ? getTag(tagId) : null
                      return tag && isFieldVisible(profileData.genderVisibility) && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Gender Identity
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                            <Tooltip title={tag.description || ''} arrow placement="top">
                              <Chip
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {tag.icon && <span>{tag.icon}</span>}
                                    <span>{tag.displayName || tag.name}</span>
                                  </Box>
                                }
                                sx={{
                                  backgroundColor: tag.color ? `${tag.color}20` : undefined,
                                  borderColor: tag.color || undefined,
                                  '&:hover': {
                                    backgroundColor: tag.color ? `${tag.color}40` : undefined,
                                  },
                                }}
                                variant="outlined"
                              />
                            </Tooltip>
                          </Box>
                        </Box>
                      )
                    })()}
                    {/* Looking For */}
                    {(() => {
                      const tagIds = profileData?.lookingForTagIds || []
                      const tags = getTags(tagIds)
                      return tags.length > 0 && isFieldVisible(profileData.lookingForVisibility) && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Looking For
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                            {tags.map((tag) => (
                              <Tooltip key={tag.id} title={tag.description || ''} arrow placement="top">
                                <Chip
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {tag.icon && <span>{tag.icon}</span>}
                                      <span>{tag.displayName || tag.name}</span>
                                    </Box>
                                  }
                                  sx={{
                                    backgroundColor: tag.color ? `${tag.color}20` : undefined,
                                    borderColor: tag.color || undefined,
                                    '&:hover': {
                                      backgroundColor: tag.color ? `${tag.color}40` : undefined,
                                    },
                                  }}
                                  variant="outlined"
                                />
                              </Tooltip>
                            ))}
                          </Box>
                        </Box>
                      )
                    })()}
                  </Grid>

                  {/* Column 3: Interests */}
                  <Grid item xs={12} md={4}>
                    {(() => {
                      const tagIds = profileData?.interestTagIds || []
                      const tags = getTags(tagIds)
                      return tags.length > 0 && isFieldVisible(profileData.interestsVisibility) && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Interests
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                            {tags.map((tag) => (
                              <Tooltip key={tag.id} title={tag.description || ''} arrow placement="top">
                                <Chip
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {tag.icon && <span>{tag.icon}</span>}
                                      <span>{tag.displayName || tag.name}</span>
                                    </Box>
                                  }
                                  sx={{
                                    backgroundColor: tag.color ? `${tag.color}20` : undefined,
                                    borderColor: tag.color || undefined,
                                    '&:hover': {
                                      backgroundColor: tag.color ? `${tag.color}40` : undefined,
                                    },
                                  }}
                                  variant="outlined"
                                />
                              </Tooltip>
                            ))}
                          </Box>
                        </Box>
                      )
                    })()}
                  </Grid>
                </Grid>
              </Grid>

              {/* Empty state */}
              {(() => {
                const hasGender = !!profileData?.genderIdentityTagId
                const hasInterests = (profileData?.interestTagIds || []).length > 0
                const hasLookingFor = (profileData?.lookingForTagIds || []).length > 0
                const hasSocialLinks = Object.keys(profileData?.socialMediaLinks || {}).length > 0
                const hasHometown = !!profileData?.hometown

                return !profileData?.bio && !hasGender && !hasInterests && !hasLookingFor && !hasSocialLinks && !hasHometown && (
                  <Grid item xs={12}>
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <PersonOutline sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No information added yet
                      </Typography>
                      {isOwnProfile && (
                        <>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            Add some information about yourself to help others get to know you
                          </Typography>
                          <Button
                            component={RouterLink}
                            to="/profile/edit"
                            variant="contained"
                            startIcon={<Edit />}
                          >
                            Edit Profile
                          </Button>
                        </>
                      )}
                    </Box>
                  </Grid>
                )
              })()}
            </Grid>
          )}
        </Paper>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            Recent Activity
          </Typography>
          <List>
            {mockActivities.map((activity, index) => (
              <Box key={activity.id}>
                <ListItem alignItems="flex-start">
                  <ListItemAvatar>{activity.icon}</ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body1" fontWeight="bold">
                          {activity.title}
                        </Typography>
                        {activity.points && (
                          <Chip
                            label={`+${activity.points} XP`}
                            size="small"
                            color="success"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {activity.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(activity.timestamp).toLocaleString()}
                        </Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
                {index < mockActivities.length - 1 && <Divider variant="inset" component="li" />}
              </Box>
            ))}
          </List>
        </Paper>
      )}

      {activeTab === 2 && profileData && (
        <ProfileBlogTab
          userId={profileData.userId || userId || user?.userId || ''}
          isOwnProfile={isOwnProfile}
        />
      )}

      {activeTab === 3 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <EmojiEvents sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Badge Collection
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            View all your earned badges and progress
          </Typography>
          <Button component={RouterLink} to="/badges" variant="contained">
            View Badges
          </Button>
        </Paper>
      )}

      </>
      )}
    </Box>
  )
}
